import { type Question } from '@/types'

import { generateSpanField } from './generateSpanField'
import { getQuestionInfo } from './getQuestionInfo'

const extractValuesFromParentheses = (formula: string) => {
  const outerParenthesesMatch = formula.match(/\((.*)\)/s)
  if (!outerParenthesesMatch) return []
  const inside = outerParenthesesMatch[1]

  const parts: string[] = []
  let currentPart = ''
  let openParens = 0

  for (let i = 0; i < inside?.length; i++) {
    const char = inside[i]

    if (char === '(') openParens++
    if (char === ')') openParens--

    if (char === ';' && openParens === 0) {
      parts.push(currentPart.trim())
      currentPart = ''
    } else {
      currentPart += char
    }
  }

  parts.push(currentPart.trim())

  return parts
}

const extractCOUNTIFSTokens = (formula: string) => {
  const countifsMatch = formula.match(/COUNTIFS\(([^)]+)\)/)
  if (!countifsMatch) return {}

  const countifsContent = countifsMatch[1]

  const addFieldRegex = /([QR]*\d+(?:_\d+)?):([QR]*\d+(?:_\d+)?)/g
  const getAddFieldMatch = countifsContent.match(addFieldRegex) || []
  const addField = getAddFieldMatch.map((item) => item.split(':')).flat()

  const args = countifsContent.split(',')
  const criteria = args[1] || ''

  const tokenRegex = /([QR]*\d+(?:_\d+)?|>=|<=|<>|=|>|<|\d+\.?\d*)/g
  const tokens = criteria.match(tokenRegex) || []

  return { addField, tokens }

  return {}
}

const extractTokensFromExpression = (expression: string) => {
  const tokenRegex = /([QR]?\d+(?:_\d+)?|\d+\.\d+|\d+|[()+\-*/=<>])/g
  const tokenMatch = expression.match(tokenRegex)
  return tokenMatch
}

export const generateIFCOUNTIFS = (questions: Question[], formula: string) => {
  const valuesInsideParentheses = extractValuesFromParentheses(formula)

  const countIfsTokens = extractCOUNTIFSTokens(valuesInsideParentheses?.[0])

  const whenTrueTokens = extractTokensFromExpression(
    valuesInsideParentheses?.[1]
  )
  const whenFalseTokens = extractTokensFromExpression(
    valuesInsideParentheses?.[2]
  )

  const initialFieldIdentifier = countIfsTokens?.addField?.[0] || ''

  const initialField = getQuestionInfo(questions, initialFieldIdentifier)

  const endFieldIdentifier = countIfsTokens?.addField?.[1] || ''
  const endField = getQuestionInfo(questions, endFieldIdentifier)

  return `<span
      data-description="Click to change function"
      data-function="if"
      data-type="function"
    >
      IF
    </span>
    <span data-type="parentheses">(</span>
    <span
      data-description="Click and type a condition"
      data-type="wrapperPlaceholder"
    >
      <span data-type="placeholder" style="display: none;">
        add condition
      </span>
      <span
        contenteditable="true"
        data-description="Click and type to add a value when true"
        data-mask="input"
        data-order="3"
        data-type="condition"
      >
        <span class="nested-function" contenteditable="false">
          <span
            data-description="Click to change function"
            data-function="countifs"
            data-type="function"
          >
            COUNTIFS
          </span>
          <span data-type="parentheses">(</span>
          ${generateSpanField({ description: initialField?.description || '', questionId: initialField?.questionId, rowOrder: initialField?.rowOrder, order: '0', type: 'addField', value: initialFieldIdentifier })}
          <span data-type="rangeSeparator">:</span>
          ${generateSpanField({ description: endField?.description || '', questionId: endField?.questionId, rowOrder: endField?.rowOrder, order: '1', type: 'addField', value: endFieldIdentifier })}
          <span data-type="semicolon">,</span>
          <span data-placeholder="countifs" data-type="wrapperPlaceholder">
            <span
              data-color="blue"
              data-type="placeholder"
              style="display: none;"
            >
              value
            </span>
            <span
              contenteditable="true"
              data-description="Click and type to add a value"
              data-mask="input"
              data-order="2"
              data-type="value"
            >
          ${countIfsTokens?.tokens
            ?.map((token) => {
              if (/^[QR]/.test(token)) {
                const tokenInfo = getQuestionInfo(questions, token)
                return generateSpanField({
                  description: tokenInfo?.description || '',
                  type: 'addedField',
                  value: token,
                })
              }
              return token
            })
            .join('')}
            </span>
          </span>
          <span data-type="parentheses">)</span>
        </span>
      </span>
    </span>
    <span data-type="semicolon">;</span>
    <span
      data-description="Click and type to add a value when true"
      data-type="wrapperPlaceholder"
    >
      <span data-color="green" data-type="placeholder" style="display: none;">
        value when true
      </span>
      <span
        contenteditable="true"
        data-description="Click and type to add a value when true"
        data-mask="input"
        data-order="4"
        data-type="whenTrue"
      >
      ${whenTrueTokens
        ?.map((token) => {
          if (/^[QR]/.test(token)) {
            const tokenInfo = getQuestionInfo(questions, token)
            return generateSpanField({
              description: tokenInfo?.description || '',
              type: 'addedField',
              value: token,
            })
          }
          return token
        })
        .join('')}
      </span>
    </span>
    <span data-type="semicolon">;</span>
    <span
      data-description="Click and type to add a value when false"
      data-type="wrapperPlaceholder"
    >
      <span data-color="red" data-type="placeholder" style="display: none;">
        value when false
      </span>
      <span
        contenteditable="true"
        data-description="Click and type to add a value when false"
        data-mask="input"
        data-order="5"
        data-type="whenFalse"
      >
      ${whenFalseTokens
        ?.map((token) => {
          if (/^[QR]/.test(token)) {
            const tokenInfo = getQuestionInfo(questions, token)
            return generateSpanField({
              description: tokenInfo?.description || '',
              type: 'addedField',
              value: token,
            })
          }
          return token
        })
        .join('')}
      </span>
    </span>
    <span data-type="parentheses">)</span>`
}
